import { ramalUi } from '../i18n/ramal';
import { horaryUi } from '../i18n/instantHoraryCatalog';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { LocalizedAlert as Alert, LocalizedButton as Button, LocalizedChip as Chip, LocalizedMenuItem as MenuItem, LocalizedTableCell as TableCell, LocalizedTextField as TextField, LocalizedTooltip as Tooltip } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import {
  Add as AddIcon,
  Class,
  Delete,
  Edit,
  Download,
  NoteAlt,
  PictureAsPdf,
  ContactPage,
  PeopleAlt,
  ShowChart,
  Visibility
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { studioApi, studioRequest } from '../api/studioClient';
import { hasProfileReportAddress } from '../utils/reportBranding';
import { formatDisplayDate } from '../utils/dateDisplay';
import { useTranslation } from 'react-i18next';
import { normalizeLanguage } from '../i18n/languages';
import { getDefaultChartSectionPath, resolvePdfLanguage, useUserPreferences } from '../contexts/UserPreferencesContext';

type DashboardMetric = {
  label: string;
  value: number | string;
  module: string;
  icon: React.ReactElement;
};

type ActivityItem = {
  id: string;
  label: string;
  detail?: string;
  createdAt?: string;
};

type DashboardPayload = {
  counts?: Record<string, number>;
  recentCharts?: any[];
  recentActivity?: ActivityItem[];
  notificationCount?: number;
};

type SavedChart = {
  _id?: string;
  id?: string;
  chartType?: string;
  birthDetails?: {
    name?: string;
    dateOfBirth?: string;
    placeOfBirth?: {
      city?: string;
      state?: string;
      country?: string;
    };
    gender?: string;
  };
  createdAt?: string;
  updatedAt?: string;
};

type SavedChartResponse = {
  charts?: SavedChart[];
  pagination?: {
    total?: number;
    page?: number;
    pages?: number;
  };
};

const formatDate = (date?: string) => {
  return formatDisplayDate(date, { fallback: '' });
};

const chartId = (chart: SavedChart) => chart.id || chart._id || '';

const chartTypeLabel = (chartType?: string) => (chartType === 'horary' ? 'Horary' : 'Individual');

const formatPlace = (chart: SavedChart) => {
  const place = chart.birthDetails?.placeOfBirth;
  return [place?.city, place?.country].filter(Boolean).join(', ') || '-';
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useAuth();
  const { i18n } = useTranslation();
  const language = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
  const { defaultChartSection, pdfLanguageSameAsApp, pdfLanguage } = useUserPreferences();
  const pdfDownloadLanguage = resolvePdfLanguage(language, pdfLanguageSameAsApp, pdfLanguage);
  const [payload, setPayload] = useState<DashboardPayload | null>(null);
  const [savedCharts, setSavedCharts] = useState<SavedChart[]>([]);
  const [savedChartTotal, setSavedChartTotal] = useState(0);
  const [chartFilters, setChartFilters] = useState({ name: '', date: '', type: 'all' });
  const [loading, setLoading] = useState(true);
  const [chartsLoading, setChartsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const counts = useMemo(() => payload?.counts || {}, [payload?.counts]);

  const metrics = useMemo<DashboardMetric[]>(() => {
    return [
      { label: 'Saved Kundli', value: counts.charts ?? 0, module: 'charts', icon: <ShowChart /> },
      { label: 'Clients', value: counts.clients ?? 0, module: 'clients', icon: <PeopleAlt /> },
      { label: 'Consultations', value: counts.consultations ?? 0, module: 'consultations', icon: <Class /> },
      { label: 'Notes', value: counts.notes ?? 0, module: 'notes', icon: <NoteAlt /> },
      { label: 'Reports', value: counts.reports ?? counts.exports ?? 0, module: 'reports', icon: <Download /> },
      { label: 'Payments', value: counts.payments ?? 0, module: 'payments', icon: <Download /> },
    ];
  }, [counts]);

  const loadDashboard = useCallback(async () => {
    if (!state.isAuthenticated) return;
    try {
      setLoading(true);
      setError(null);
      const data = await studioRequest<DashboardPayload>({
        url: '/dashboard',
        method: 'GET',
      }, 'Failed to load Studio dashboard');
      setPayload(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load Studio dashboard');
    } finally {
      setLoading(false);
    }
  }, [state.isAuthenticated]);

  const loadSavedCharts = useCallback(async () => {
    if (!state.isAuthenticated) return;
    try {
      setChartsLoading(true);
      const data = await studioRequest<SavedChartResponse>({
        url: '/charts',
        method: 'GET',
        params: {
          page: 1,
          limit: 50,
          name: chartFilters.name || undefined,
          date: chartFilters.date || undefined,
          type: chartFilters.type === 'all' ? undefined : chartFilters.type,
        },
      }, 'Failed to load saved Kundli');
      setSavedCharts(data.charts || []);
      setSavedChartTotal(data.pagination?.total || (data.charts || []).length);
    } catch (err: any) {
      setError(err.message || 'Failed to load saved Kundli');
    } finally {
      setChartsLoading(false);
    }
  }, [chartFilters.date, chartFilters.name, chartFilters.type, state.isAuthenticated]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard, state.currentWorkspaceId]);

  useEffect(() => {
    loadSavedCharts();
  }, [loadSavedCharts, state.currentWorkspaceId]);

  const clearChartFilters = () => {
    setChartFilters({ name: '', date: '', type: 'all' });
  };

  const downloadChartPdf = async (chart: SavedChart, clientReport = false) => {
    const id = chartId(chart);
    if (!id) return;
    if (!hasProfileReportAddress(state.user)) {
      const openProfile = window.confirm(
        'Add your address in My Profile before downloading the report so it can appear on the PDF. Open My Profile now?'
      );
      if (openProfile) navigate('/profile');
      return;
    }
    try {
      const response = await studioApi.get(`/charts/${id}/pdf`, { responseType: 'blob', params: { language: pdfDownloadLanguage, ...(clientReport ? { clientReport: 1 } : {}) } });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${chart.birthDetails?.name || id}_${clientReport ? 'client_report' : 'report'}.pdf`.replace(/\s+/g, '_');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || 'Failed to download PDF');
    }
  };

  const deleteSavedChart = async (chart: SavedChart) => {
    const id = chartId(chart);
    if (!id || !window.confirm('Delete this saved Kundli?')) return;
    try {
      await studioRequest({ url: `/charts/${id}`, method: 'DELETE' }, 'Failed to delete saved Kundli');
      await Promise.all([loadDashboard(), loadSavedCharts()]);
    } catch (err: any) {
      setError(err.message || 'Failed to delete saved Kundli');
    }
  };

  const dashboardPathForModule = (module: string) => {
    if (module === 'charts') return '/dashboard';
    if (module === 'planner' || module === 'calendar') return '/planner';
    return `/cms/${module}`;
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>Kundlistar Studio</Typography>
          <Typography color="text.secondary">Personal astrology workspace</Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/calculate')}>
            Create Kundli
          </Button>
        </Stack>
      </Stack>

      <Card variant="outlined" sx={{ mb: 3 }}><CardContent>
        <Typography variant="h6">{horaryUi(i18n.language, 'Instant Horary')}</Typography>
        <Typography color="text.secondary">{horaryUi(i18n.language, 'Ask a question · Choose a seed · Get Yes/No')}</Typography>
        <Button sx={{ mt: 1 }} onClick={() => navigate('/instant-horary')}>{horaryUi(i18n.language, 'Show Answer')}</Button>
      </CardContent></Card>
      <Card variant="outlined" sx={{ mb: 3 }}><CardContent>
        <Typography variant="h6">{ramalUi(i18n.language, 'title')}</Typography>
        <Typography color="text.secondary">{ramalUi(i18n.language, 'subtitle')}</Typography>
        <Button sx={{ mt: 1 }} onClick={() => navigate('/ramal')}>{ramalUi(i18n.language, 'open')}</Button>
      </CardContent></Card>

      {error && <Alert severity="warning" sx={{ mb: 3 }} action={<Button onClick={() => window.location.reload()}>Retry</Button>}>{error}</Alert>}

      {loading ? (
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))', lg: 'repeat(6, minmax(0, 1fr))' }, gap: 1.25 }}>
          {metrics.map((metric) => (
            <Box key={`${metric.label}-${metric.module}`}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75, minHeight: 30 }}>
                    <Box sx={{ color: "var(--app-ink-primary)", display: 'flex', '& svg': { fontSize: 20 } }}>{metric.icon}</Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.15 }}>
                      {metric.label}
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Typography variant="h5" sx={{ fontWeight: 600, lineHeight: 1 }}>{metric.value}</Typography>
                    <Button
                      size="small"
                      onClick={() => navigate(dashboardPathForModule(metric.module))}
                      sx={{ minWidth: 0, px: 1 }}
                    >
                      Open
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
      )}

      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>मेरे चार्ट / My Charts</Typography>
              <Typography variant="body2" color="text.secondary">{savedChartTotal} saved Kundli</Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/calculate')}>Create Kundli</Button>
            </Stack>
          </Stack>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(220px, 1.35fr) minmax(180px, 1.35fr) minmax(180px, 1fr) auto' }, gap: 2, mb: 2 }}>
            <TextField
              label="नाम से खोजें / Search by Name"
              value={chartFilters.name}
              onChange={(event) => setChartFilters((prev) => ({ ...prev, name: event.target.value }))}
            />
            <TextField
              label="तिथि / Date"
              type="date"
              value={chartFilters.date}
              onChange={(event) => setChartFilters((prev) => ({ ...prev, date: event.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              select
              label="प्रकार / Type"
              value={chartFilters.type}
              onChange={(event) => setChartFilters((prev) => ({ ...prev, type: event.target.value }))}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="individual">Individual</MenuItem>
              <MenuItem value="horary">Horary</MenuItem>
            </TextField>
            <Button variant="outlined" onClick={clearChartFilters} sx={{ minHeight: 56 }}>
              Clear
            </Button>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>नाम / Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>प्रकार / Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>स्थान / Place</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>लिंग / Gender</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>बनाया / Created</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {chartsLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : savedCharts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Alert severity="info">No saved Kundli found.</Alert>
                    </TableCell>
                  </TableRow>
                ) : (
                  savedCharts.map((chart) => {
                    const id = chartId(chart);
                    return (
                      <TableRow key={id} hover>
                        <TableCell>{chart.birthDetails?.name || 'Untitled Kundli'}</TableCell>
                        <TableCell>
                          <Chip size="small" variant="outlined" color="primary" label={chartTypeLabel(chart.chartType)} />
                        </TableCell>
                        <TableCell>{formatPlace(chart)}</TableCell>
                        <TableCell>
                          {chart.birthDetails?.gender ? <Chip size="small" color="primary" label={chart.birthDetails.gender} /> : '-'}
                        </TableCell>
                        <TableCell>{formatDate(chart.createdAt)}</TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                            <Tooltip title="Open Kundli">
                              <IconButton color="primary" onClick={() => navigate(getDefaultChartSectionPath(id, chart.chartType === 'horary' && defaultChartSection !== 'kp' ? 'nadi' : defaultChartSection))}>
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                            {chart.chartType !== 'horary' && (
                              <Tooltip title="Edit Kundli">
                                <IconButton color="primary" onClick={() => navigate(`/calculate?edit=${encodeURIComponent(id)}`)}>
                                  <Edit />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Download Client Report">
                              <IconButton color="warning" onClick={() => downloadChartPdf(chart, true)}>
                                <ContactPage />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download PDF">
                              <IconButton color="primary" onClick={() => downloadChartPdf(chart)}>
                                <PictureAsPdf />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Kundli">
                              <IconButton color="error" onClick={() => deleteSavedChart(chart)}>
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2, mt: 3 }}>
        <Box>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Recent Activity</Typography>
              {(payload?.recentActivity || []).length === 0 ? (
                <Alert severity="info">Workspace activity will appear here as charts, notes, consultations, and tasks change.</Alert>
              ) : (
                <Stack spacing={1.25}>
                  {(payload?.recentActivity || []).slice(0, 6).map((item) => (
                    <Box key={item.id}>
                      <Typography sx={{ fontWeight: 600 }}>{item.label}</Typography>
                      <Typography variant="body2" color="text.secondary">{item.detail} {formatDate(item.createdAt)}</Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Container>
  );
};

export default Dashboard;
